import {
  createRouter,
  createRoute,
  createRootRoute,
  Outlet,
  lazyRouteComponent,
} from '@tanstack/react-router';
import { AppInitializer } from './components/AppInitializer';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ErrorPage } from '@/pages/Error/ErrorPage';
import { API_CONFIG } from './shared/config/api.config';
import AuthGuard from '@/features/auth/components/AuthGuard';

// Create root route
const rootRoute = createRootRoute({
  component: () => (
    <AppInitializer>
      <Outlet />
    </AppInitializer>
  ),
  errorComponent: ({ error, reset }: { error: Error; reset: () => void }) => (
    <ErrorBoundary error={error} reset={reset} />
  ),
  notFoundComponent: () => <ErrorPage />,
});

// Create layout route (parent for all app routes)
const layoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'layout',
  component: lazyRouteComponent(() =>
    import('./components/Layout/Layout').then(module => ({
      default: module.default,
    }))
  ),
});

// Create index route (home/chat)
const indexRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/',
  validateSearch: (search: Record<string, unknown>) => {
    return {
      chat: (search.chat as string) || undefined,
      assistant: (search.assistant as string) || undefined,
      assistantError: (search.assistantError as string) || undefined,
    };
  },
  component: lazyRouteComponent(async () => {
    const { default: Chat } = await import('./pages/Chat/Chat');
    if (API_CONFIG.ENABLE_AUTH) {
      return {
        default: () => (
          <AuthGuard>
            <Chat />
          </AuthGuard>
        ),
      };
    }
    return { default: Chat };
  }),
});

// Create assistant hub route
const assistantHubRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/assistant-hub',
  component: lazyRouteComponent(async () => {
    const { default: AssistantHubRoute } = await import(
      './pages/AssistantHub/index'
    );
    return {
      default: () => (
        <AuthGuard requiredPermission='assistant-hub:access'>
          <AssistantHubRoute />
        </AuthGuard>
      ),
    };
  }),
});

// Create create assistant route
const createAssistantRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/create-assistant',
  validateSearch: (search: Record<string, unknown>) => {
    return {
      edit: (search.edit as string) || undefined,
      tab: (search.tab as string) || undefined,
    };
  },
  component: lazyRouteComponent(async () => {
    const { default: CreateAssistant } = await import(
      './pages/CreateAssistant/CreateAssistant'
    );
    if (API_CONFIG.ENABLE_AUTH) {
      return {
        default: () => (
          <AuthGuard requiredPermission='assistant-hub:access'>
            <CreateAssistant />
          </AuthGuard>
        ),
      };
    }
    return { default: CreateAssistant };
  }),
});

// Create settings route
const settingsRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/settings',
  component: lazyRouteComponent(async () => {
    const { SettingsPage } = await import('./features/settings');
    if (API_CONFIG.ENABLE_AUTH) {
      return {
        default: () => (
          <AuthGuard requiredPermission='assistant-hub:access'>
            <SettingsPage />
          </AuthGuard>
        ),
      };
    }
    return { default: SettingsPage };
  }),
});

// Create updates route

const updatesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/updates',
  component: lazyRouteComponent(() => import('./pages/Updates/Updates')),
});

// Create all updates route
const allUpdatesRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/updates/all',
  component: lazyRouteComponent(() => import('./pages/Updates/AllUpdates')),
});

// Create admin panel route
const adminPanelRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: '/admin-panel',
  component: lazyRouteComponent(async () => {
    const { default: AdminPanel } = await import(
      './pages/AdminPanel/AdminPanel'
    );
    if (API_CONFIG.ENABLE_AUTH) {
      return {
        default: () => (
          <AuthGuard
            requiredRoles={['gamuda-tenant-admin']}
            unauthorizedRedirect='/'
          >
            <AdminPanel />
          </AuthGuard>
        ),
      };
    }
    return { default: AdminPanel };
  }),
});

// Create login route
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: lazyRouteComponent(
    () => import('@/features/auth/components/Login')
  ),
});

// Create auth callback route for OAuth
const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: lazyRouteComponent(
    () => import('@/features/auth/components/AuthGuard')
  ),
});

// Create logout route
const logoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/logout',
  component: lazyRouteComponent(
    () => import('@/features/auth/components/Logout')
  ),
});

// Create tenant limit exceeded route
const tenantLimitExceededRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/tenant-limit-exceeded',
  component: lazyRouteComponent(
    () => import('@/features/auth/components/TenantLimitExceeded')
  ),
});

// Create the route tree
const routeTree = rootRoute.addChildren([
  loginRoute,
  authCallbackRoute,
  logoutRoute,
  tenantLimitExceededRoute,
  layoutRoute.addChildren([
    indexRoute,
    assistantHubRoute,
    createAssistantRoute,
    settingsRoute,
    updatesRoute,
    allUpdatesRoute,
    adminPanelRoute,
  ]),
]);

// Default loading indicator shown during lazy route transitions
const RouteLoadingIndicator = () => (
  <div className='flex h-full w-full items-center justify-center'>
    <div className='h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-[#0087d4]' />
  </div>
);

// Create the router
export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPendingComponent: RouteLoadingIndicator,
  defaultPendingMinMs: 0,
  defaultNotFoundComponent: () => <ErrorPage />,
});

// Type declaration for type-safe routing
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
